import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import {
    getPartnerIsStatisticsLoading,
    getPartnerStatistics,
    getPartnerStatisticsFilters,
    getPartnerStatisticsTotal,
} from '../Partner/partnerSelectors.js';
import {
    PARTNER_STATISTICS_CAMPAIGN_COLUMNS_GROUP_TYPES,
    PARTNER_STATISTICS_CLIENTS_COLUMN_GROUP_TYPES,
    PARTNER_STATISTICS_REGISTERED_AT_COLUMN_GROUP_TYPES,
    PARTNER_STATISTICS_REGISTERED_COLUMN_GROUP_TYPES,
} from './statisticsConstants.js';
import {
    DATE_PRESETS_CONFIG,
    DEFAULT_FILTER_PARAMS,
    buildStatisticsLoadParams,
    formatRegisteredAtColumn,
    getStatisticsLoadParamsFromSaved,
    GROUP_CONFIG,
    parseFilterEndDate,
    parseFilterStartDate,
} from './statisticsUtils.js';
import { loadPartnerStatisticsAction } from '../Partner/partnerActions.js';
import amountFormat from '../../utils/amountFormat.js';
import { formatTurnoverLots } from '../../utils/turnoverToLots.js';
import DatePicker from '../../components/DatePicker/DatePicker.jsx';
import {
    StatisticsFieldRowStyled,
    StatisticsFiltersGridStyled,
    StatisticsGroupByStyled,
    StatisticsPageStyled,
    StatisticsPresetsStyled,
    StatisticsSectionStyled,
    StatisticsTableStyled,
    StatisticsTableWrapStyled,
} from './styled/StatisticsStyled';

const SORTED_GROUP_BY_CONFIG_VALUES = _.sortBy(Object.values(GROUP_CONFIG), 'order');

function Statistics() {
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const statistics = useSelector(getPartnerStatistics);
    const statisticsTotal = useSelector(getPartnerStatisticsTotal);
    const isPartnerStatisticsLoading = useSelector(getPartnerIsStatisticsLoading);
    const savedFilters = useSelector(getPartnerStatisticsFilters);

    const [startDate, setStartDate] = useState(() => parseFilterStartDate(savedFilters));
    const [endDate, setEndDate] = useState(() => parseFilterEndDate(savedFilters));
    const [groupType, setGroupType] = useState(
        () => savedFilters?.group_type || DEFAULT_FILTER_PARAMS.group_type,
    );
    const [draftGroupType, setDraftGroupType] = useState(
        () => savedFilters?.group_type || DEFAULT_FILTER_PARAMS.group_type,
    );
    const [campaignName, setCampaignName] = useState(
        () => savedFilters?.campaign_name || DEFAULT_FILTER_PARAMS.campaign_name,
    );
    const [campaignChannel, setCampaignChannel] = useState(
        () => savedFilters?.campaign_channel || DEFAULT_FILTER_PARAMS.campaign_channel,
    );
    const groupTypeConfig = useMemo(() => GROUP_CONFIG[groupType], [groupType]);

    useEffect(() => {
        dispatch(loadPartnerStatisticsAction(getStatisticsLoadParamsFromSaved(savedFilters)));
        // Restore last applied filters on mount; do not re-run when Redux filters update after submit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleDateStartChange = useCallback(
        (date) => {
            setStartDate(date);
            if (moment(endDate).isBefore(moment(date))) {
                setEndDate(date);
            }
        },
        [endDate],
    );

    const handleDateEndChange = useCallback((date) => {
        setEndDate(date);
    }, []);

    const handlePresetClick = useCallback((e) => {
        const presetName = e.currentTarget.dataset.name;
        const preset = _.find(DATE_PRESETS_CONFIG, { name: presetName });
        setStartDate(preset?.startDate()?.toDate() || null);
        setEndDate(preset?.endDate()?.toDate() || null);
    }, []);

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const currentGroupType = e.target.querySelector('[name=group_type]:checked').value;
            setGroupType(currentGroupType);
            setDraftGroupType(currentGroupType);
            dispatch(
                loadPartnerStatisticsAction(
                    buildStatisticsLoadParams({
                        groupType: currentGroupType,
                        startDate,
                        endDate,
                        campaignName,
                        campaignChannel,
                    }),
                ),
            );
        },
        [campaignChannel, campaignName, dispatch, endDate, startDate],
    );

    const formatGroupColumnValue = useCallback(
        (value) => groupTypeConfig.format(value),
        [groupTypeConfig],
    );

    const isShowCampaignsColumns = useMemo(
        () => PARTNER_STATISTICS_CAMPAIGN_COLUMNS_GROUP_TYPES.includes(groupType),
        [groupType],
    );
    const isShowClientsColumn = useMemo(
        () => PARTNER_STATISTICS_CLIENTS_COLUMN_GROUP_TYPES.includes(groupType),
        [groupType],
    );
    const isShowRegisteredColumn = useMemo(
        () => PARTNER_STATISTICS_REGISTERED_COLUMN_GROUP_TYPES.includes(groupType),
        [groupType],
    );
    const isShowRegisteredAtColumn = useMemo(
        () => PARTNER_STATISTICS_REGISTERED_AT_COLUMN_GROUP_TYPES.includes(groupType),
        [groupType],
    );

    const hasRows = statistics.length > 0;

    return (
        <StatisticsPageStyled>
            <div className="page-header">
                <span className="section-eyebrow">{getLangPath('statistics.eyebrow')}</span>
                <h1 className="page-title">{getLangPath('statistics.title')}</h1>
                <p className="page-desc">{getLangPath('statistics.desc')}</p>
            </div>

            <StatisticsSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('statistics.filters_step')}</span>
                        <h2 className="section-title">{getLangPath('statistics.filters_title')}</h2>
                        <p className="section-desc">{getLangPath('statistics.filters_desc')}</p>
                    </div>
                </div>

                <Form onSubmit={handleSubmit}>
                    <StatisticsFiltersGridStyled>
                        <StatisticsFieldRowStyled>
                            <FormGroup>
                                <Form.Label>{getLangPath('statistics.start_date')}</Form.Label>
                                <DatePicker
                                    selectedDate={startDate}
                                    onChange={handleDateStartChange}
                                    popperPlacement="bottom"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                />
                            </FormGroup>
                            <FormGroup>
                                <Form.Label>{getLangPath('statistics.end_date')}</Form.Label>
                                <DatePicker
                                    selectedDate={endDate}
                                    onChange={handleDateEndChange}
                                    popperPlacement="bottom"
                                    minDate={startDate}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                />
                            </FormGroup>
                        </StatisticsFieldRowStyled>

                        <StatisticsFieldRowStyled>
                            <FormGroup>
                                <Form.Label>{getLangPath('campaigns.campaign_name')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="campaign_name"
                                    value={campaignName}
                                    onChange={(e) => setCampaignName(e.target.value)}
                                    placeholder={getLangPath('statistics.campaign_name_placeholder')}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Form.Label>{getLangPath('campaigns.campaign_channel')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="campaign_channel"
                                    value={campaignChannel}
                                    onChange={(e) => setCampaignChannel(e.target.value)}
                                    placeholder={getLangPath('statistics.campaign_channel_placeholder')}
                                />
                            </FormGroup>
                        </StatisticsFieldRowStyled>
                    </StatisticsFiltersGridStyled>

                    <StatisticsFiltersGridStyled className="mt-4">
                        <FormGroup>
                            <Form.Label className="mb-2">{getLangPath('statistics.date_presets')}</Form.Label>
                            <StatisticsPresetsStyled>
                                {DATE_PRESETS_CONFIG.map((preset) => (
                                    <Button
                                        key={preset.name}
                                        variant="link"
                                        type="button"
                                        data-name={preset.name}
                                        onClick={handlePresetClick}
                                    >
                                        {getLangPath(preset.title)}
                                    </Button>
                                ))}
                            </StatisticsPresetsStyled>
                        </FormGroup>

                        <FormGroup>
                            <Form.Label className="mb-0">{getLangPath('statistics.group_by')}</Form.Label>
                            <StatisticsGroupByStyled>
                                {SORTED_GROUP_BY_CONFIG_VALUES.map(({ type, title }) => (
                                    <Form.Check
                                        checked={type === draftGroupType}
                                        onChange={() => setDraftGroupType(type)}
                                        key={type}
                                        inline
                                        type="radio"
                                        name="group_type"
                                        id={`statistics-group-${type}`}
                                        value={type}
                                        label={getLangPath(title)}
                                    />
                                ))}
                            </StatisticsGroupByStyled>
                        </FormGroup>
                    </StatisticsFiltersGridStyled>

                    <div className="section-actions">
                        <Button variant="primary" type="submit" disabled={isPartnerStatisticsLoading}>
                            {getLangPath('statistics.apply_filter')}
                        </Button>
                    </div>
                </Form>
            </StatisticsSectionStyled>

            <StatisticsSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('statistics.results_step')}</span>
                        <h2 className="section-title">{getLangPath('statistics.results_title')}</h2>
                        <p className="section-desc">{getLangPath('statistics.results_desc')}</p>
                    </div>
                </div>

                <StatisticsTableWrapStyled>
                    <StatisticsTableStyled>
                        <thead>
                            <tr>
                                <th>{getLangPath(groupTypeConfig.title)}</th>
                                {isShowRegisteredAtColumn && <th>{getLangPath('statistics.registered_at')}</th>}
                                {isShowCampaignsColumns && <th>{getLangPath('campaigns.campaign_name')}</th>}
                                {isShowCampaignsColumns && <th>{getLangPath('campaigns.campaign_channel')}</th>}
                                {isShowClientsColumn && (
                                    <th className="col-metric">{getLangPath('statistics.clients')}</th>
                                )}
                                {isShowRegisteredColumn && (
                                    <th className="col-metric">{getLangPath('statistics.registered')}</th>
                                )}
                                <th className="col-metric">{getLangPath('statistics.ftd')}</th>
                                <th className="col-money">{getLangPath('statistics.ftd_sum')}</th>
                                <th className="col-metric">{getLangPath('statistics.deposits')}</th>
                                <th className="col-money">{getLangPath('statistics.deposits_sum')}</th>
                                <th className="col-money">{getLangPath('statistics.turnover')}</th>
                                <th className="col-money">{getLangPath('statistics.inout')}</th>
                                <th className="col-money">{getLangPath('statistics.revshare')}</th>
                                <th className="col-money">{getLangPath('statistics.profit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hasRows ? (
                                statistics.map((item, index) => (
                                    <tr key={index}>
                                        <td>{formatGroupColumnValue(item[groupTypeConfig.field]) || ''}</td>
                                        {isShowRegisteredAtColumn && (
                                            <td>{formatRegisteredAtColumn(item.registered_at)}</td>
                                        )}
                                        {isShowCampaignsColumns && <td>{item.campaign_name || ''}</td>}
                                        {isShowCampaignsColumns && <td>{item.campaign_channel || ''}</td>}
                                        {isShowClientsColumn && (
                                            <td className="col-metric">{item.clients}</td>
                                        )}
                                        {isShowRegisteredColumn && (
                                            <td className="col-metric">{item.registered}</td>
                                        )}
                                        <td className="col-metric">{item.ftd}</td>
                                        <td className="col-money">${amountFormat(item.ftd_sum)}</td>
                                        <td className="col-metric">{item.deposits}</td>
                                        <td className="col-money">${amountFormat(item.deposits_sum)}</td>
                                        <td className="col-money">{formatTurnoverLots(item.turnover)}</td>
                                        <td className="col-money">${amountFormat(item.profit_inout)}</td>
                                        <td className="col-money">${amountFormat(item.profit_revshare)}</td>
                                        <td className="col-money">${amountFormat(item.profit)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        className="empty-cell"
                                        colSpan={
                                            9 +
                                            (isShowRegisteredAtColumn ? 1 : 0) +
                                            (isShowCampaignsColumns ? 2 : 0) +
                                            (isShowClientsColumn ? 1 : 0) +
                                            (isShowRegisteredColumn ? 1 : 0)
                                        }
                                    >
                                        {getLangPath('statistics.empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {hasRows && (
                            <tfoot>
                                <tr>
                                    <td>{getLangPath('statistics.total')}</td>
                                    {isShowRegisteredAtColumn && <td />}
                                    {isShowCampaignsColumns && <td />}
                                    {isShowCampaignsColumns && <td />}
                                    {isShowClientsColumn && (
                                        <td className="col-metric">{statisticsTotal.clients}</td>
                                    )}
                                    {isShowRegisteredColumn && (
                                        <td className="col-metric">{statisticsTotal.registered}</td>
                                    )}
                                    <td className="col-metric">{statisticsTotal.ftd}</td>
                                    <td className="col-money">${amountFormat(statisticsTotal.ftd_sum)}</td>
                                    <td className="col-metric">{statisticsTotal.deposits}</td>
                                    <td className="col-money">${amountFormat(statisticsTotal.deposits_sum)}</td>
                                    <td className="col-money">{formatTurnoverLots(statisticsTotal.turnover)}</td>
                                    <td className="col-money">${amountFormat(statisticsTotal.profit_inout)}</td>
                                    <td className="col-money">${amountFormat(statisticsTotal.profit_revshare)}</td>
                                    <td className="col-money">${amountFormat(statisticsTotal.profit)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </StatisticsTableStyled>
                </StatisticsTableWrapStyled>
            </StatisticsSectionStyled>
        </StatisticsPageStyled>
    );
}

export default Statistics;
